import { SSEStatusEvent } from '../models/chat.models';
import { MessageActivityItem, MessageActivityMetadata, MessageProgressKind, MessageProgressStep, MessageProgressTool } from '../models/message-progress-step.model';

const PROGRESS_PHASES = ['thinking', 'tool_started', 'tool_finished'] as const;
const PROGRESS_KINDS: MessageProgressKind[] = ['data_query', 'documentation', 'context', 'other'];

export function isProgressPhase(phase: string): boolean {
  return (PROGRESS_PHASES as readonly string[]).includes(phase);
}

/**
 * Folds one `status` event into the steps shown so far and returns the new list, or the same list
 * when the event carries nothing usable. `tool_finished` updates the chip with the same `step` in
 * place; one for a step never started is kept as a finished chip rather than dropped.
 */
export function applyProgressStatus(steps: MessageProgressStep[] | undefined, event: SSEStatusEvent): MessageProgressStep[] | undefined {
  const current = steps ?? [];
  if (typeof event.step !== 'number') {
    return steps;
  }

  switch (event.phase) {
    case 'thinking':
      return event.text ? [...current, { step: event.step, type: 'thinking', text: event.text }] : steps;
    case 'tool_started':
      return event.label ? [...current, buildTool(event.step, event.kind, event.label, true)] : steps;
    case 'tool_finished':
      return finishTool(current, event);
    default:
      return steps;
  }
}

/** Stops every spinner once the turn is over, since a chip never finished is not still running. */
export function settleProgress(steps: MessageProgressStep[] | undefined): MessageProgressStep[] | undefined {
  if (!steps?.some(step => step.type === 'tool' && step.inProgress)) {
    return steps;
  }

  return steps.map(step => (step.type === 'tool' && step.inProgress ? { ...step, inProgress: false } : step));
}

/** The steps of a reloaded row's `metadata.activity`, all finished. Undefined when the row has none. */
export function readProgressFromMetadata(activity: MessageActivityMetadata | null | undefined): MessageProgressStep[] | undefined {
  const items = activity?.items;
  if (!Array.isArray(items) || items.length === 0) {
    return undefined;
  }

  const steps = items.map(readProgressItem).filter((step): step is MessageProgressStep => step !== null);
  return steps.length > 0 ? steps : undefined;
}

export function summarizeProgress(steps: MessageProgressStep[]): string {
  return steps.length === 1 ? 'Worked for 1 step' : `Worked for ${steps.length} steps`;
}

function finishTool(steps: MessageProgressStep[], event: SSEStatusEvent): MessageProgressStep[] {
  const finished: Partial<MessageProgressTool> = { inProgress: false, ok: event.ok !== false, durationMs: event.duration_ms };
  const started = steps.find((step): step is MessageProgressTool => step.type === 'tool' && step.step === event.step);
  if (started) {
    const updated: MessageProgressTool = { ...started, ...finished };
    return steps.map(step => (step === started ? updated : step));
  }

  return event.label ? [...steps, { ...buildTool(event.step as number, event.kind, event.label, false), ...finished }] : steps;
}

function readProgressItem(item: MessageActivityItem): MessageProgressStep | null {
  if (typeof item.step !== 'number') {
    return null;
  }
  if (item.type === 'thinking') {
    return item.text ? { step: item.step, type: 'thinking', text: item.text } : null;
  }
  if (item.type === 'tool') {
    return item.label ? { ...buildTool(item.step, item.kind, item.label, false), ok: item.ok !== false, durationMs: item.duration_ms } : null;
  }
  return null;
}

function buildTool(step: number, kind: string | undefined, label: string, inProgress: boolean): MessageProgressTool {
  return { step, type: 'tool', kind: readKind(kind), label, inProgress };
}

function readKind(kind: string | undefined): MessageProgressKind {
  return PROGRESS_KINDS.includes(kind as MessageProgressKind) ? (kind as MessageProgressKind) : 'other';
}
